import React, { useState, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { CheckCircle, Waves, Package, ChevronRight, ChevronLeft, CreditCard, User, Phone, Mail, Clock, AlertCircle, Hash, Download, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : 'http://localhost:8001/api';

const LOCATIONS = [
  { value: 'san_antolin', label: 'San Antolín' },
  { value: 'san_martin', label: 'San Martín' },
  { value: 'ria_niembro', label: 'Ría de Niembro (SUP)' },
];

const COURSE_BRACKETS = [
  { days: 1, price: 35 },
  { days: 2, price: 65 },
  { days: 5, price: 140 },
  { days: 7, price: 170 },
];

const RENTAL_PRICES = {
  wetsuit:   { half: 10, full: 15, label: 'Neopreno' },
  surfboard: { half: 20, full: 25, label: 'Tabla de Surf' },
  sup:       { half: 30, full: 40, label: 'Paddle Surf (SUP)' },
};

function calcCoursePrice(days) {
  if (days <= 0) return 0;
  const exact = COURSE_BRACKETS.find(b => b.days === days);
  if (exact) return exact.price;
  const above = COURSE_BRACKETS.find(b => b.days > days);
  if (above) return Math.round((days / above.days) * above.price * 100) / 100;
  const max = COURSE_BRACKETS[COURSE_BRACKETS.length - 1];
  return Math.round((days / max.days) * max.price * 100) / 100;
}

function calcRentalPrice(items) {
  return items.reduce((sum, item) => {
    const base = RENTAL_PRICES[item.type];
    if (!base) return sum;
    return sum + (item.duration === 'half' ? base.half : base.full) * (item.quantity || 1);
  }, 0);
}

const fmtDate = (d) => { if (!d) return ''; const [y, m, day] = d.split('-'); return `${day}/${m}/${y}`; };
const fmtEur = (n) => `${Number(n || 0).toFixed(2).replace('.', ',')} €`;
const WEEKDAYS_ES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MONTHS_ES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

function DatePicker({ selected, onChange, maxDates = 14 }) {
  const today = new Date();
  const [viewMonth, setViewMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const todayStr = today.toISOString().split('T')[0];
  const daysInMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0).getDate();
  const firstDay = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1).getDay();

  const toggleDate = (dateStr) => {
    if (dateStr < todayStr) return;
    if (selected.includes(dateStr)) { onChange(selected.filter(d => d !== dateStr)); return; }
    if (selected.length >= maxDates) { toast.error(`Máximo ${maxDates} días`); return; }
    onChange([...selected, dateStr].sort());
  };

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${viewMonth.getFullYear()}-${String(viewMonth.getMonth()+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
    cells.push(dateStr);
  }

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
      <div className="flex items-center justify-between px-4 py-3 bg-teal-50 border-b border-gray-200">
        <button onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth()-1, 1))} className="p-1 rounded hover:bg-teal-100 text-teal-700">‹</button>
        <span className="font-semibold text-teal-800 text-sm">{MONTHS_ES[viewMonth.getMonth()]} {viewMonth.getFullYear()}</span>
        <button onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth()+1, 1))} className="p-1 rounded hover:bg-teal-100 text-teal-700">›</button>
      </div>
      <div className="grid grid-cols-7 text-center">
        {WEEKDAYS_ES.map(w => <div key={w} className="py-1 text-xs font-medium text-gray-400">{w}</div>)}
        {cells.map((dateStr, i) => {
          if (!dateStr) return <div key={`e-${i}`} />;
          const isPast = dateStr < todayStr;
          const isSel = selected.includes(dateStr);
          return (
            <button key={dateStr} onClick={() => toggleDate(dateStr)} disabled={isPast}
              className={`py-2 text-sm rounded-lg m-0.5 transition-all
                ${isPast ? 'text-gray-300 cursor-not-allowed' : ''}
                ${isSel ? 'bg-teal-600 text-white font-bold shadow' : ''}
                ${!isPast && !isSel ? 'hover:bg-teal-100 text-gray-700' : ''}
                ${dateStr === todayStr && !isSel ? 'ring-1 ring-teal-400' : ''}`}>
              {parseInt(dateStr.split('-')[2])}
            </button>
          );
        })}
      </div>
      {selected.length > 0 && (
        <div className="px-3 py-2 bg-teal-50 border-t border-gray-200 text-xs text-teal-700">
          {selected.length} día{selected.length > 1 ? 's' : ''} seleccionado{selected.length > 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}

// ── PDF receipt generator ────────────────────────────────────────────────────
async function generateReceiptPDF(successData) {
  const doc = new jsPDF({ format: 'a4' });
  const W = doc.internal.pageSize.getWidth();

  // Try to embed the logo
  try {
    const blob = await fetch('/logo_surfskull.png').then(r => r.blob());
    const b64 = await new Promise(res => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.readAsDataURL(blob); });
    doc.addImage(b64, 'PNG', 14, 12, 18, 18);
  } catch (_) {}

  // Header
  doc.setFontSize(16); doc.setFont('helvetica', 'bold');
  doc.setTextColor(26, 43, 60);
  doc.text('SURF SKULL', 36, 20);
  doc.setFontSize(8); doc.setFont('helvetica', 'normal');
  doc.setTextColor(138, 155, 168);
  doc.text('Playa de San Antolín · Llanes, Asturias', 36, 26);
  doc.text('681 208 167 · info@surfskull.es', 36, 31);

  // Title
  doc.setFontSize(18); doc.setFont('helvetica', 'bold');
  doc.setTextColor(74, 109, 124);
  doc.text('COMPROBANTE DE RESERVA', W / 2, 48, { align: 'center' });

  // Divider
  doc.setDrawColor(229, 224, 216);
  doc.line(14, 53, W - 14, 53);

  // Meta info block
  const metaY = 60;
  doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(74, 93, 108);
  doc.text(`Fecha de emisión: ${new Date().toLocaleDateString('es-ES')}`, 14, metaY);
  if (successData.code) {
    doc.setFont('helvetica', 'bold'); doc.setTextColor(74, 109, 124);
    doc.text(`Código de reserva: ${successData.code}`, W - 14, metaY, { align: 'right' });
    doc.setFont('helvetica', 'normal'); doc.setTextColor(74, 93, 108);
  }

  // Client & reservation block
  const blockY = metaY + 12;
  doc.setFillColor(245, 242, 237);
  doc.roundedRect(14, blockY, W - 28, 32, 3, 3, 'F');
  doc.setFontSize(8); doc.setTextColor(138, 155, 168);
  doc.text('TITULAR', 20, blockY + 8);
  doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(26, 43, 60);
  doc.text(successData.name || '—', 20, blockY + 16);
  if (successData.phone) { doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(74, 93, 108); doc.text(successData.phone, 20, blockY + 23); }

  const locLabel = LOCATIONS.find(l => l.value === (successData.location || successData.course?.location))?.label || '';
  if (locLabel) { doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(74, 93, 108); doc.text(locLabel, W - 20, blockY + 16, { align: 'right' }); }

  // Items table
  const tableY = blockY + 40;
  const rows = [];

  if (successData.type === 'course') {
    const dates = successData.batch_dates?.length ? successData.batch_dates : (successData.dates || []);
    const dateStr = dates.length > 1
      ? `${fmtDate(dates[0])} – ${fmtDate(dates[dates.length - 1])} (${dates.length} días)`
      : fmtDate(dates[0] || successData.course?.date || '');
    const timeStr = successData.course?.course_time || successData.course?.time || '';
    rows.push(['Curso de Surf', `${dateStr}${timeStr ? ' · ' + timeStr : ''}`, successData.students > 1 ? `${successData.students} alumnos` : '1 alumno', fmtEur(successData.totalPrice)]);
  } else {
    const dates = successData.dates || [];
    const dateStr = dates.length > 1 ? `${fmtDate(dates[0])} – ${fmtDate(dates[dates.length-1])} (${dates.length} días)` : fmtDate(dates[0] || '');
    (successData.items || []).forEach(item => {
      const info = RENTAL_PRICES[item.type];
      if (!info) return;
      rows.push([info.label, `${dateStr} · ${item.duration === 'half' ? 'Medio día' : 'Día completo'}`, `× ${item.quantity || 1}`, fmtEur((item.duration === 'half' ? info.half : info.full) * (item.quantity||1) * dates.length)]);
    });
  }

  autoTable(doc, {
    startY: tableY,
    head: [['Concepto', 'Detalle', 'Cantidad', 'Importe']],
    body: rows,
    theme: 'grid',
    headStyles: { fillColor: [74, 109, 124], textColor: 255, fontSize: 9, fontStyle: 'bold' },
    bodyStyles: { fontSize: 9, textColor: [26, 43, 60] },
    columnStyles: { 3: { halign: 'right', fontStyle: 'bold' } },
    margin: { left: 14, right: 14 },
  });

  const finalY = doc.lastAutoTable.finalY + 8;

  // Totals
  const paid = parseFloat(successData.amountPaid || 0);
  const total = parseFloat(successData.totalPrice || 0);
  const pending = Math.max(0, total - paid);

  autoTable(doc, {
    startY: finalY,
    body: [
      ['Total reserva', fmtEur(total)],
      ['Importe pagado', fmtEur(paid)],
      ...(pending > 0 ? [['Pendiente de pago', fmtEur(pending)]] : []),
    ],
    theme: 'plain',
    bodyStyles: { fontSize: 10 },
    columnStyles: {
      0: { fontStyle: 'bold', textColor: [74, 93, 108] },
      1: { halign: 'right', fontStyle: 'bold', textColor: [26, 43, 60] },
    },
    margin: { left: W - 100, right: 14 },
  });

  // Payment status badge
  const badgeY = doc.lastAutoTable.finalY + 6;
  if (pending <= 0) {
    doc.setFillColor(220, 252, 231); doc.roundedRect(W - 14 - 60, badgeY - 6, 60, 10, 2, 2, 'F');
    doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(21, 128, 61);
    doc.text('✓ PAGADO', W - 14 - 30, badgeY, { align: 'center' });
  } else {
    doc.setFillColor(254, 226, 226); doc.roundedRect(W - 14 - 70, badgeY - 6, 70, 10, 2, 2, 'F');
    doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(185, 28, 28);
    doc.text('PENDIENTE DE PAGO', W - 14 - 35, badgeY, { align: 'center' });
  }

  // Footer
  const footerY = doc.internal.pageSize.getHeight() - 20;
  doc.setDrawColor(229, 224, 216); doc.line(14, footerY - 4, W - 14, footerY - 4);
  doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(138, 155, 168);
  doc.text('Surf Skull · Escuela de Surf en Llanes · surfskull.es', W / 2, footerY, { align: 'center' });
  doc.text('Este documento es un comprobante de reserva, no una factura fiscal.', W / 2, footerY + 5, { align: 'center' });

  doc.save(`comprobante_surfskull_${(successData.name || 'reserva').replace(/\s+/g, '_').toLowerCase()}.pdf`);
}

// ── Main component ───────────────────────────────────────────────────────────
export default function PayPage() {
  const [step, setStep] = useState('type');
  const [serviceType, setServiceType] = useState(null);
  const [courseMode, setCourseMode] = useState('code');

  // Code search
  const [codeInput, setCodeInput] = useState('');
  const [codeSearching, setCodeSearching] = useState(false);
  const [foundReservation, setFoundReservation] = useState(null);
  const [codeError, setCodeError] = useState('');

  // New course form
  const [courseForm, setCourseForm] = useState({ reservation_name: '', phone: '', email: '', location: 'san_antolin', dates: [], num_students: 1 });

  // Rental form
  const [rentalForm, setRentalForm] = useState({ client_name: '', phone: '', email: '', location: 'san_antolin', dates: [], items: [{ type: 'surfboard', quantity: 1, duration: 'full' }] });

  // Payment
  const [cardForm, setCardForm] = useState({ name: '', number: '', expiry: '', cvc: '' });
  const [submitting, setSubmitting] = useState(false);
  const [successData, setSuccessData] = useState(null);

  const coursePrice = courseMode === 'new'
    ? calcCoursePrice(courseForm.dates.length) * courseForm.num_students
    : foundReservation ? parseFloat(foundReservation.data?.amount_pending || 0) : 0;
  const rentalPrice = calcRentalPrice(rentalForm.items) * (rentalForm.dates.length || 1);
  const totalPrice = serviceType === 'course' ? coursePrice : rentalPrice;

  const handleCodeSearch = async () => {
    if (codeInput.trim().length < 4) { setCodeError('Introduce el código completo'); return; }
    setCodeSearching(true); setCodeError(''); setFoundReservation(null);
    try {
      const r = await fetch(`${API}/public/by-code?code=${encodeURIComponent(codeInput.trim().toUpperCase())}`);
      if (!r.ok) { setCodeError('Código no encontrado. Comprueba que sea correcto.'); return; }
      const data = await r.json();
      if (parseFloat(data.data?.amount_pending || 0) <= 0 && parseFloat(data.data?.amount_paid || 0) > 0) {
        setCodeError('Esta reserva ya está pagada.');
        return;
      }
      setFoundReservation(data);
    } catch { setCodeError('Error de conexión. Inténtalo de nuevo.'); }
    finally { setCodeSearching(false); }
  };

  const handlePayment = async () => {
    if (!cardForm.name || !cardForm.number || !cardForm.expiry || !cardForm.cvc) {
      toast.error('Completa los datos de la tarjeta'); return;
    }
    setSubmitting(true);
    try {
      let result;
      if (serviceType === 'course') {
        if (courseMode === 'code' && foundReservation) {
          const res = foundReservation.data;
          const r = await fetch(`${API}/public/pay-reservation`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ reservation_id: res.id, amount_paid: parseFloat(res.amount_pending || 0), payment_method: 'web_demo' }),
          });
          result = await r.json();
          if (!result.ok) throw new Error(result.detail || 'Error');
          setSuccessData({
            type: 'course', name: res.reservation_name, code: res.payment_code,
            phone: res.phone, location: res.course?.location,
            course: res.course, batch_dates: res.batch_dates || [],
            students: res.num_students || 1, items: [],
            totalPrice: parseFloat(res.income_total || 0),
            amountPaid: parseFloat(res.income_total || 0),
          });
        } else {
          if (!courseForm.reservation_name || courseForm.dates.length === 0) { toast.error('Completa nombre y fechas'); setSubmitting(false); return; }
          const r = await fetch(`${API}/public/book-course`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ reservation_name: courseForm.reservation_name, phone: courseForm.phone, email: courseForm.email, location: courseForm.location, specific_dates: courseForm.dates, num_students: courseForm.num_students, students: [], income_total: coursePrice, amount_paid: coursePrice, payment_method: 'web_demo' }),
          });
          result = await r.json();
          if (!result.ok) throw new Error(result.detail || 'Error');
          setSuccessData({ type: 'course', name: courseForm.reservation_name, phone: courseForm.phone, location: courseForm.location, batch_dates: courseForm.dates, students: courseForm.num_students, items: [], totalPrice: coursePrice, amountPaid: coursePrice });
        }
      } else {
        if (!rentalForm.client_name || rentalForm.dates.length === 0) { toast.error('Completa nombre y fechas'); setSubmitting(false); return; }
        const r = await fetch(`${API}/public/book-rental`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ client_name: rentalForm.client_name, phone: rentalForm.phone, email: rentalForm.email, location: rentalForm.location, specific_dates: rentalForm.dates, duration: rentalForm.items[0]?.duration || 'full', items: rentalForm.items, income_total: rentalPrice, amount_paid: rentalPrice, payment_method: 'web_demo' }),
        });
        result = await r.json();
        if (!result.ok) throw new Error(result.detail || 'Error');
        setSuccessData({ type: 'rental', name: rentalForm.client_name, phone: rentalForm.phone, location: rentalForm.location, dates: rentalForm.dates, items: rentalForm.items, totalPrice: rentalPrice, amountPaid: rentalPrice });
      }
      setStep('success');
    } catch (e) { toast.error(e.message || 'Error al procesar el pago'); }
    finally { setSubmitting(false); }
  };

  const formatCardNumber = (v) => v.replace(/\D/g, '').slice(0, 16).replace(/(.{4})/g, '$1 ').trim();
  const formatExpiry = (v) => { const n = v.replace(/\D/g, '').slice(0, 4); return n.length > 2 ? `${n.slice(0, 2)}/${n.slice(2)}` : n; };

  const resetAll = () => {
    setStep('type'); setServiceType(null); setCourseMode('code');
    setCodeInput(''); setFoundReservation(null); setCodeError('');
    setCourseForm({ reservation_name: '', phone: '', email: '', location: 'san_antolin', dates: [], num_students: 1 });
    setRentalForm({ client_name: '', phone: '', email: '', location: 'san_antolin', dates: [], items: [{ type: 'surfboard', quantity: 1, duration: 'full' }] });
    setCardForm({ name: '', number: '', expiry: '', cvc: '' });
    setSuccessData(null);
  };

  // ── RENDER ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-teal-50 to-cyan-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-teal-100">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-3">
          <img src="/logo_surfskull.png" alt="Surf Skull" className="h-10 w-auto" />
          <div>
            <div className="font-bold text-gray-800 text-lg leading-tight">Surf Skull</div>
            <div className="text-xs text-teal-600">Escuela de Surf · Llanes, Asturias</div>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-8">

        {/* ── TYPE SELECTION ── */}
        {step === 'type' && (
          <div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">¿Qué quieres reservar?</h1>
            <p className="text-gray-500 text-sm mb-8">Selecciona el tipo de servicio para continuar</p>
            <div className="grid grid-cols-2 gap-4 mb-8">
              <button onClick={() => { setServiceType('course'); setCourseMode('code'); setStep('course'); }}
                className="flex flex-col items-center gap-3 p-6 bg-white rounded-2xl border-2 border-transparent hover:border-teal-400 shadow hover:shadow-md transition-all group">
                <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center group-hover:bg-teal-200 transition-colors">
                  <Waves className="w-7 h-7 text-teal-600" />
                </div>
                <div className="text-center">
                  <div className="font-bold text-gray-800">Curso de Surf</div>
                  <div className="text-xs text-gray-500 mt-1">Desde 35 €/día</div>
                </div>
              </button>
              <button onClick={() => { setServiceType('rental'); setStep('rental'); }}
                className="flex flex-col items-center gap-3 p-6 bg-white rounded-2xl border-2 border-transparent hover:border-teal-400 shadow hover:shadow-md transition-all group">
                <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center group-hover:bg-teal-200 transition-colors">
                  <Package className="w-7 h-7 text-teal-600" />
                </div>
                <div className="text-center">
                  <div className="font-bold text-gray-800">Alquiler</div>
                  <div className="text-xs text-gray-500 mt-1">Tabla · Neopreno · SUP</div>
                </div>
              </button>
            </div>
            {/* Price table */}
            <div className="bg-white rounded-2xl shadow p-5">
              <div className="font-semibold text-gray-700 mb-3 text-sm">Tarifas de cursos</div>
              <div className="grid grid-cols-4 gap-2">
                {COURSE_BRACKETS.map(b => (
                  <div key={b.days} className="text-center bg-teal-50 rounded-lg py-3">
                    <div className="text-lg font-bold text-teal-700">{b.days}d</div>
                    <div className="text-xs font-semibold text-gray-800">{b.price} €</div>
                    <div className="text-xs text-gray-400">{(b.price/b.days).toFixed(0)} €/d</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 text-xs text-gray-400 text-center">Precio por persona · Tabla, neopreno y monitor incluidos</div>
            </div>
          </div>
        )}

        {/* ── COURSE ── */}
        {step === 'course' && (
          <div>
            <button onClick={() => setStep('type')} className="flex items-center gap-1 text-sm text-teal-600 hover:text-teal-800 mb-4">
              <ChevronLeft className="w-4 h-4" /> Atrás
            </button>
            <h1 className="text-xl font-bold text-gray-800 mb-1">Curso de Surf</h1>
            <p className="text-gray-500 text-sm mb-5">¿Ya tienes una reserva o quieres crear una nueva?</p>

            <div className="flex rounded-xl overflow-hidden border border-gray-200 mb-6">
              <button onClick={() => setCourseMode('code')}
                className={`flex-1 py-2.5 text-sm font-medium transition-colors ${courseMode === 'code' ? 'bg-teal-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                Tengo reserva
              </button>
              <button onClick={() => setCourseMode('new')}
                className={`flex-1 py-2.5 text-sm font-medium transition-colors ${courseMode === 'new' ? 'bg-teal-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                Nueva reserva
              </button>
            </div>

            {/* CODE SEARCH */}
            {courseMode === 'code' && (
              <div>
                <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 mb-5">
                  <div className="flex items-start gap-2 text-sm text-teal-800">
                    <Hash className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <p>Introduce el <strong>código de reserva</strong> que te ha dado la escuela para localizar tu reserva y pagar.</p>
                  </div>
                </div>
                <Label className="text-sm font-medium text-gray-700 mb-2 block">Código de reserva</Label>
                <div className="flex gap-2 mb-3">
                  <input
                    className="flex-1 px-4 py-3 border border-gray-200 rounded-xl text-lg font-mono tracking-widest uppercase text-center focus:outline-none focus:ring-2 focus:ring-teal-400"
                    placeholder="XXXXXXXX"
                    value={codeInput}
                    maxLength={8}
                    onChange={e => { setCodeInput(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')); setCodeError(''); setFoundReservation(null); }}
                    onKeyDown={e => e.key === 'Enter' && handleCodeSearch()}
                  />
                  <Button onClick={handleCodeSearch} disabled={codeSearching || codeInput.length < 4}
                    className="bg-teal-600 hover:bg-teal-700 text-white px-5 rounded-xl">
                    {codeSearching ? '…' : 'Buscar'}
                  </Button>
                </div>
                {codeError && (
                  <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" /> {codeError}
                  </div>
                )}
                {foundReservation && (
                  <div className="bg-white border-2 border-teal-400 rounded-xl p-4 mb-5">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="font-bold text-gray-800 text-lg">{foundReservation.data.reservation_name}</div>
                        <div className="text-sm text-gray-500 mt-0.5 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {LOCATIONS.find(l => l.value === foundReservation.data.course?.location)?.label || '—'}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-400">Pendiente</div>
                        <div className="text-2xl font-bold text-teal-600">{fmtEur(foundReservation.data.amount_pending)}</div>
                        <div className="text-xs text-gray-400">de {fmtEur(foundReservation.data.income_total)} total</div>
                      </div>
                    </div>
                    {/* Dates */}
                    {foundReservation.data.batch_dates?.length > 0 && (
                      <div className="text-sm text-gray-600 mb-2">
                        <span className="font-medium">Fechas:</span>{' '}
                        {foundReservation.data.batch_dates.length > 1
                          ? `${fmtDate(foundReservation.data.batch_dates[0])} – ${fmtDate(foundReservation.data.batch_dates[foundReservation.data.batch_dates.length-1])} (${foundReservation.data.batch_dates.length} días)`
                          : fmtDate(foundReservation.data.batch_dates[0])}
                      </div>
                    )}
                    {/* Schedule status */}
                    {foundReservation.data.course && (
                      <div className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg mt-2
                        ${foundReservation.data.course.schedule_confirmed ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                        {foundReservation.data.course.schedule_confirmed
                          ? <><CheckCircle className="w-3 h-3" /> Horario confirmado: {foundReservation.data.course.time}</>
                          : <><Clock className="w-3 h-3" /> La escuela confirmará el horario próximamente</>
                        }
                      </div>
                    )}
                    <Button onClick={() => setStep('payment')} className="w-full mt-4 bg-teal-600 hover:bg-teal-700 text-white rounded-xl">
                      Pagar {fmtEur(foundReservation.data.amount_pending)} <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* NEW COURSE */}
            {courseMode === 'new' && (
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-1 block">Nombre completo *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                      placeholder="Nombre y apellidos" value={courseForm.reservation_name}
                      onChange={e => setCourseForm(p => ({ ...p, reservation_name: e.target.value }))} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-1 block">Teléfono</Label>
                    <div className="relative"><Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                        placeholder="600 000 000" value={courseForm.phone}
                        onChange={e => setCourseForm(p => ({ ...p, phone: e.target.value }))} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-1 block">Email</Label>
                    <div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                        placeholder="tu@email.com" value={courseForm.email}
                        onChange={e => setCourseForm(p => ({ ...p, email: e.target.value }))} />
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-1 block">Ubicación</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {LOCATIONS.map(l => (
                      <button key={l.value} onClick={() => setCourseForm(p => ({ ...p, location: l.value }))}
                        className={`py-2 px-2 text-xs rounded-xl border-2 font-medium transition-all ${courseForm.location === l.value ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-gray-200 text-gray-600 hover:border-teal-300'}`}>
                        {l.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-1 block">Número de alumnos</Label>
                  <div className="flex items-center gap-3">
                    <button onClick={() => setCourseForm(p => ({ ...p, num_students: Math.max(1, p.num_students-1) }))} className="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center text-lg font-bold hover:bg-gray-50">−</button>
                    <span className="text-xl font-bold text-gray-800 w-6 text-center">{courseForm.num_students}</span>
                    <button onClick={() => setCourseForm(p => ({ ...p, num_students: p.num_students+1 }))} className="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center text-lg font-bold hover:bg-gray-50">+</button>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-1 block">Selecciona los días del curso *</Label>
                  <DatePicker selected={courseForm.dates} onChange={dates => setCourseForm(p => ({ ...p, dates }))} />
                </div>
                {courseForm.dates.length > 0 && (
                  <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
                    <div className="flex justify-between items-center">
                      <div className="text-sm text-gray-600">{courseForm.dates.length}d × {courseForm.num_students} alumno{courseForm.num_students > 1 ? 's' : ''}</div>
                      <div className="text-xl font-bold text-teal-700">{fmtEur(coursePrice)}</div>
                    </div>
                    {![1,2,5,7].includes(courseForm.dates.length) && (
                      <div className="text-xs text-amber-600 mt-1">Precio proporcional al bono de {COURSE_BRACKETS.find(b => b.days >= courseForm.dates.length)?.days || 7} días</div>
                    )}
                  </div>
                )}
                <Button onClick={() => setStep('payment')} disabled={!courseForm.reservation_name || courseForm.dates.length === 0}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-xl py-3 disabled:opacity-40">
                  Continuar al pago <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* ── RENTAL ── */}
        {step === 'rental' && (
          <div>
            <button onClick={() => setStep('type')} className="flex items-center gap-1 text-sm text-teal-600 hover:text-teal-800 mb-4">
              <ChevronLeft className="w-4 h-4" /> Atrás
            </button>
            <h1 className="text-xl font-bold text-gray-800 mb-1">Alquiler de Material</h1>
            <p className="text-gray-500 text-sm mb-5">Selecciona el material, los días y la duración</p>
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-1 block">Nombre completo *</Label>
                <div className="relative"><User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                    placeholder="Nombre y apellidos" value={rentalForm.client_name}
                    onChange={e => setRentalForm(p => ({ ...p, client_name: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-1 block">Teléfono</Label>
                  <input className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-400" placeholder="600 000 000" value={rentalForm.phone} onChange={e => setRentalForm(p => ({ ...p, phone: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-1 block">Email</Label>
                  <input className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-400" placeholder="tu@email.com" value={rentalForm.email} onChange={e => setRentalForm(p => ({ ...p, email: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-1 block">Ubicación</Label>
                <div className="grid grid-cols-3 gap-2">
                  {LOCATIONS.map(l => (
                    <button key={l.value} onClick={() => setRentalForm(p => ({ ...p, location: l.value }))}
                      className={`py-2 px-2 text-xs rounded-xl border-2 font-medium transition-all ${rentalForm.location === l.value ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-gray-200 text-gray-600 hover:border-teal-300'}`}>
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-2 block">Material a alquilar</Label>
                {rentalForm.items.map((item, idx) => (
                  <div key={idx} className="bg-white border border-gray-200 rounded-xl p-4 mb-2">
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {Object.entries(RENTAL_PRICES).map(([type, info]) => (
                        <button key={type} onClick={() => { const items=[...rentalForm.items]; items[idx]={...items[idx],type}; setRentalForm(p=>({...p,items})); }}
                          className={`py-2 text-xs rounded-lg border-2 font-medium transition-all ${item.type===type ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-gray-200 text-gray-600 hover:border-teal-300'}`}>
                          {info.label}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="text-xs text-gray-500 mb-1">Duración</div>
                        <div className="flex rounded-lg overflow-hidden border border-gray-200">
                          {[['half','Medio día'],['full','Día completo']].map(([val,lbl]) => (
                            <button key={val} onClick={() => { const items=[...rentalForm.items]; items[idx]={...items[idx],duration:val}; setRentalForm(p=>({...p,items})); }}
                              className={`flex-1 py-1.5 text-xs font-medium transition-colors ${item.duration===val ? 'bg-teal-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                              {lbl}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Cantidad</div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => { const items=[...rentalForm.items]; items[idx]={...items[idx],quantity:Math.max(1,(items[idx].quantity||1)-1)}; setRentalForm(p=>({...p,items})); }} className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center font-bold hover:bg-gray-50">−</button>
                          <span className="font-bold text-gray-800">{item.quantity||1}</span>
                          <button onClick={() => { const items=[...rentalForm.items]; items[idx]={...items[idx],quantity:(items[idx].quantity||1)+1}; setRentalForm(p=>({...p,items})); }} className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center font-bold hover:bg-gray-50">+</button>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-400">Por día</div>
                        <div className="font-bold text-teal-700 text-sm">{fmtEur(RENTAL_PRICES[item.type]?.[item.duration] * (item.quantity||1))}</div>
                      </div>
                    </div>
                    {rentalForm.items.length > 1 && <button onClick={() => setRentalForm(p => ({ ...p, items: p.items.filter((_,i) => i!==idx) }))} className="mt-2 text-xs text-red-400 hover:text-red-600">Eliminar</button>}
                  </div>
                ))}
                <button onClick={() => setRentalForm(p => ({ ...p, items: [...p.items, { type:'surfboard', quantity:1, duration:'full' }] }))}
                  className="w-full py-2 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-400 hover:border-teal-300 hover:text-teal-600 transition-all">
                  + Añadir otro artículo
                </button>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-1 block">Días de alquiler *</Label>
                <DatePicker selected={rentalForm.dates} onChange={dates => setRentalForm(p => ({ ...p, dates }))} />
              </div>
              {rentalForm.dates.length > 0 && (
                <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">{rentalForm.dates.length} día{rentalForm.dates.length > 1 ? 's' : ''}</span>
                    <span className="text-xl font-bold text-teal-700">{fmtEur(rentalPrice)}</span>
                  </div>
                </div>
              )}
              <Button onClick={() => setStep('payment')} disabled={!rentalForm.client_name || rentalForm.dates.length === 0}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-xl py-3 disabled:opacity-40">
                Continuar al pago <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* ── PAYMENT ── */}
        {step === 'payment' && (
          <div>
            <button onClick={() => setStep(serviceType)} className="flex items-center gap-1 text-sm text-teal-600 hover:text-teal-800 mb-4">
              <ChevronLeft className="w-4 h-4" /> Atrás
            </button>
            <h1 className="text-xl font-bold text-gray-800 mb-1">Pago seguro</h1>
            <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-5 flex items-center gap-1.5">
              <AlertCircle className="w-4 h-4 flex-shrink-0" /> Modo demo — no se realizará ningún cargo real
            </p>
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 mb-5">
              <div className="text-xs font-semibold text-teal-700 uppercase tracking-wide mb-2">Resumen</div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">{serviceType === 'course' ? '🏄 Curso de surf' : '🤙 Alquiler de material'}</span>
                <span className="font-bold text-gray-800">{fmtEur(totalPrice)}</span>
              </div>
              <div className="text-xs text-gray-400">
                {serviceType === 'course' && courseMode === 'new' && `${courseForm.dates.length}d · ${courseForm.num_students} alumno${courseForm.num_students>1?'s':''} · ${courseForm.reservation_name}`}
                {serviceType === 'course' && courseMode === 'code' && foundReservation?.data?.reservation_name}
                {serviceType === 'rental' && `${rentalForm.dates.length}d · ${rentalForm.client_name}`}
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
              <div className="flex items-center gap-2 mb-1">
                <CreditCard className="w-5 h-5 text-teal-600" />
                <span className="font-semibold text-gray-700">Datos de tarjeta</span>
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Nombre en la tarjeta</Label>
                <input className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                  placeholder="NOMBRE APELLIDO" value={cardForm.name}
                  onChange={e => setCardForm(p => ({ ...p, name: e.target.value.toUpperCase() }))} />
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Número de tarjeta</Label>
                <input className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-teal-400"
                  placeholder="1234 5678 9012 3456" value={cardForm.number}
                  onChange={e => setCardForm(p => ({ ...p, number: formatCardNumber(e.target.value) }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">Caducidad</Label>
                  <input className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-400"
                    placeholder="MM/AA" value={cardForm.expiry}
                    onChange={e => setCardForm(p => ({ ...p, expiry: formatExpiry(e.target.value) }))} />
                </div>
                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">CVV</Label>
                  <input className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-400"
                    placeholder="123" maxLength={4} value={cardForm.cvc}
                    onChange={e => setCardForm(p => ({ ...p, cvc: e.target.value.replace(/\D/g,'').slice(0,4) }))} />
                </div>
              </div>
            </div>
            <Button onClick={handlePayment} disabled={submitting}
              className="w-full mt-5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl py-3 text-base font-semibold disabled:opacity-50">
              {submitting ? 'Procesando…' : `Pagar ${fmtEur(totalPrice)}`}
            </Button>
            <p className="text-center text-xs text-gray-400 mt-3">🔒 Pago cifrado y seguro</p>
          </div>
        )}

        {/* ── SUCCESS + RECEIPT ── */}
        {step === 'success' && successData && (
          <div>
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-800 mb-1">¡Reserva confirmada!</h1>
              <p className="text-gray-500">Gracias, <strong>{successData.name}</strong></p>
            </div>

            {/* Receipt card */}
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden mb-5 shadow-sm">
              {/* Header receipt */}
              <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-5 py-4 flex items-center gap-3">
                <img src="/logo_surfskull.png" alt="Surf Skull" className="h-10 w-auto rounded" />
                <div>
                  <div className="font-bold text-white text-lg">Surf Skull</div>
                  <div className="text-teal-200 text-xs">Escuela de Surf · Llanes, Asturias</div>
                </div>
              </div>

              <div className="px-5 py-4 space-y-3">
                {/* Code */}
                {successData.code && (
                  <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                    <span className="text-xs text-gray-500">Código de reserva</span>
                    <span className="font-mono font-bold text-[#4A6D7C] tracking-widest text-sm">{successData.code}</span>
                  </div>
                )}

                {/* Client */}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Titular</span>
                  <span className="font-semibold text-gray-800">{successData.name}</span>
                </div>
                {successData.phone && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Teléfono</span>
                    <span className="text-gray-700">{successData.phone}</span>
                  </div>
                )}

                <hr className="border-gray-100" />

                {/* Service details */}
                {successData.type === 'course' && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Servicio</span>
                      <span className="font-semibold text-gray-800">Curso de Surf</span>
                    </div>
                    {successData.batch_dates?.length > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Fechas</span>
                        <span className="text-gray-700 text-right">
                          {successData.batch_dates.length > 1
                            ? `${fmtDate(successData.batch_dates[0])} – ${fmtDate(successData.batch_dates[successData.batch_dates.length-1])}`
                            : fmtDate(successData.batch_dates[0])
                          }
                          <span className="text-gray-400 ml-1">({successData.batch_dates.length}d)</span>
                        </span>
                      </div>
                    )}
                    {successData.students > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Alumnos</span>
                        <span className="text-gray-700">{successData.students}</span>
                      </div>
                    )}
                    {(successData.location || successData.course?.location) && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Ubicación</span>
                        <span className="text-gray-700">{LOCATIONS.find(l => l.value === (successData.location || successData.course?.location))?.label}</span>
                      </div>
                    )}
                    {/* Schedule status */}
                    {successData.course && (
                      <div className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg
                        ${successData.course.schedule_confirmed ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                        {successData.course.schedule_confirmed
                          ? <><CheckCircle className="w-3 h-3" /> Horario confirmado: {successData.course.time}</>
                          : <><Clock className="w-3 h-3" /> La escuela confirmará el horario próximamente</>
                        }
                      </div>
                    )}
                  </>
                )}

                {successData.type === 'rental' && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Servicio</span>
                      <span className="font-semibold text-gray-800">Alquiler de Material</span>
                    </div>
                    {successData.dates?.length > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Fechas</span>
                        <span className="text-gray-700">
                          {successData.dates.length > 1 ? `${fmtDate(successData.dates[0])} – ${fmtDate(successData.dates[successData.dates.length-1])}` : fmtDate(successData.dates[0])}
                        </span>
                      </div>
                    )}
                    {(successData.items || []).map((item, i) => {
                      const info = RENTAL_PRICES[item.type];
                      if (!info) return null;
                      return (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-gray-500">{info.label} × {item.quantity || 1}</span>
                          <span className="text-gray-700">{item.duration === 'half' ? 'Medio día' : 'Día completo'}</span>
                        </div>
                      );
                    })}
                  </>
                )}

                <hr className="border-gray-100" />

                {/* Totals */}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Total reserva</span>
                  <span className="font-semibold text-gray-800">{fmtEur(successData.totalPrice)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Importe pagado</span>
                  <span className="font-bold text-green-600">{fmtEur(successData.amountPaid)}</span>
                </div>
                {(successData.totalPrice - successData.amountPaid) > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Pendiente</span>
                    <span className="font-bold text-red-500">{fmtEur(successData.totalPrice - successData.amountPaid)}</span>
                  </div>
                )}
                <div className={`text-center font-bold text-sm py-2 rounded-lg ${successData.amountPaid >= successData.totalPrice ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                  {successData.amountPaid >= successData.totalPrice ? '✓ PAGADO' : 'PAGO PARCIAL'}
                </div>
              </div>

              {/* Footer receipt */}
              <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 text-xs text-gray-400 text-center">
                681 208 167 · info@surfskull.es · surfskull.es
              </div>
            </div>

            {/* Actions */}
            <Button onClick={() => generateReceiptPDF(successData)}
              className="w-full mb-3 bg-[#4A6D7C] hover:bg-[#3A5D6C] text-white rounded-xl py-3 flex items-center justify-center gap-2">
              <Download className="w-4 h-4" /> Descargar comprobante PDF
            </Button>
            <Button onClick={resetAll} variant="outline" className="w-full rounded-xl">
              Nueva reserva
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
